<template lang="pug">
#cooperation-systems-list(v-cloak)
  .box
    .box-header.d-flex.flex-row
      .titles
        .title
          span システム連携
      .flex-fill.d-flex.flex-row-reverse
        button.btn.request-btn(@click="gotoSupport" type="button") システム連携のお問い合わせ
    .box-body
      .mb-2 下表のシステムとの連携希望がございましたら、新規お問い合わせよりご相談ください。
      CooperationSystemTable(v-model="items" :fields="fields" :isLoading="isLoading")
      span ※ 記載の連携費用はHitomeQ コネクト側の連携費用です。連携システム側の連携費用は連携システムの方にお問い合わせください。
      br
      span ※「ケア樹」は株式会社グッドツリーの商標又は登録商標です。
      br
      span ※「SCOP receipt」は株式会社善光総合研究所の商標又は登録商標です。
      br
      span ※「i-MEDIC Plus 介護」は株式会社レゾナの商標又は登録商標です。
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";

import PageLoading from "@/pc/basics/PageLoading.vue";
import CooperationSystemTable, { CooperationSystemTableField, CooperationSystemTableItem } from "@/pc/containers/settings/CooperationSystemTable.vue";

/**
 * ### システム連携一覧コンポーネント
 */
@Component({
  components: {
    PageLoading,
    CooperationSystemTable
  }
})
export default class CooperationSystemList extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** テーブル列名 */
  fields: Array<CooperationSystemTableField> = [];
  /** テーブル各行の情報 */
  items: Array<CooperationSystemTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /**
   * ### vue lifecycle mounted
   */
  public mounted(): void {
    this.isLoading = true;

    const fields: Array<CooperationSystemTableField> = this.createFields();
    this.fields = fields;
    const items: Array<CooperationSystemTableItem> = this.createItems(userStore.facility);
    this.items = items;

    this.isLoading = false;
    return;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(): Array<CooperationSystemTableField> {
    let fields: Array<CooperationSystemTableField> = [];

    fields.push({ key: "systemName", label: "システム名", sortable: false, sortDirection: "desc", class: "system-name" });
    fields.push({ key: "systemType", label: "システム種別", sortable: false, sortDirection: "desc", class: "system-type" });
    fields.push({ key: "functionName", label: "連携機能", sortable: false, sortDirection: "desc", class: "function-name" });
    fields.push({ key: "use", label: "アカウントの連携設定状態", sortable: false, sortDirection: "desc", class: "use" });
    fields.push({ key: "cost", label: "連携費用", sortable: false, sortDirection: "desc", class: "cost" });

    return fields;
  }

  /**
   * ### テーブル行を作成する
   */
  public createItems(facility: FacilityGetModel): Array<CooperationSystemTableItem> {
    const items: Array<CooperationSystemTableItem> = [];

    // 並び順や文言を調整する必要があるのでベタ書き
    items.push({
      companyName: "コニカミノルタ",
      systemName: "HitomeQ ケアサポート（クラウド版）",
      systemUrl: "https://www.konicaminolta.com/jp-ja/care-support/service/hitomeq-caresupport/",
      systemType: "見守り・介護業務支援",
      functionName: "利用者",
      use: facility.cooperationSystems.hitomeQCareSupport,
      cost: "無料（連携追加費用はありません）"
    });
    items.push({
      companyName: "コニカミノルタ",
      systemName: "HitomeQ ツリー",
      systemUrl: "https://qol.konicaminolta.jp/hitomeq/hitomeq-tree",
      systemType: "介護記録請求ソフト",
      functionName: "文書送付",
      use: facility.cooperationSystems.hitomeQTree,
      cost: "無料（連携追加費用はありません）"
    });
    items.push({
      companyName: "グッドツリー",
      systemName: "ケア樹",
      systemUrl: "https://caretree.jp/",
      systemType: "介護記録請求ソフト",
      functionName: "文書送付",
      use: facility.cooperationSystems.careTree,
      cost: "無料（連携追加費用はありません）"
    });
    items.push({
      companyName: "株式会社善光総合研究所",
      systemName: "SCOP receipt",
      systemUrl: "https://scop.sfri.jp/solution#receipt",
      systemType: "請求ソフト",
      functionName: "文書送付",
      use: facility.cooperationSystems.scopReceipt,
      cost: "無料（連携追加費用はありません）"
    });
    items.push({
      companyName: "レゾナ",
      systemName: "i-MEDIC Plus 介護",
      systemUrl: "https://www.imedic-plus.com/kaigo.php",
      systemType: "介護記録請求ソフト",
      functionName: "利用者、文書送付",
      use: facility.cooperationSystems.iMedic,
      cost: "無料（連携追加費用はありません）"
    });

    return items;
  }

  /**
   * ### 新規お問い合わせ画面へ遷移する
   */
  public gotoSupport(): void {
    this.$router.push({ name: PcUrl.SupportRegister, params: { type: "5" } });
  }
}
</script>

<style lang="scss" scoped>
#cooperation-systems-list {
  max-width: 1080px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    width: 100%;
    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }
}
</style>
